import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { Paper, Box } from '@mui/material';
import {
  formatRupeesIndianCompact,
  premiumTooltip,
  standardGrid,
  indianValueAxis,
  indianCategoryAxis,
  STATUS_COLORS,
  defaultAnimation,
} from './echartsConfig';
import { reportChartCanvasSx, reportChartPaperSx } from './reportChartLayout';
import ReportCardHeader, { ReportInsightBanner } from './ReportCardHeader';
import ReportKpiStrip, { kpiMapToItems } from './ReportKpiStrip';

const toNum = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const norm = s => String(s || '').toLowerCase().trim();

function datasetIndices(datasets) {
  const idx = needle => datasets.findIndex(d => norm(d.label).includes(needle));
  return { iConf: idx('confirm'), iTent: idx('tent'), iWait: idx('wait') };
}

function rowTotal(datasets, idxMap, i) {
  const { iConf, iTent, iWait } = idxMap;
  let t = 0;
  if (iConf >= 0) t += toNum(datasets[iConf].data[i]);
  if (iTent >= 0) t += toNum(datasets[iTent].data[i]);
  if (iWait >= 0) t += toNum(datasets[iWait].data[i]);
  return t;
}

export function prepareSegmentDisplay(data) {
  const rawLabels = (data?.labels || []).map(l => String(l ?? ''));
  const rawDatasets = (data?.datasets || []).map(ds => ({
    ...ds,
    data: (ds.data || []).map(toNum),
  }));

  const idxMap = datasetIndices(rawDatasets);
  const n = rawLabels.length;

  if (n === 0) {
    return { labels: [], datasets: [], rowTotals: [], grandTotal: 0, segmentCount: 0 };
  }

  const rows = [];
  for (let i = 0; i < n; i++) {
    rows.push({
      label: rawLabels[i],
      conf: idxMap.iConf >= 0 ? toNum(rawDatasets[idxMap.iConf].data[i]) : 0,
      tent: idxMap.iTent >= 0 ? toNum(rawDatasets[idxMap.iTent].data[i]) : 0,
      wait: idxMap.iWait >= 0 ? toNum(rawDatasets[idxMap.iWait].data[i]) : 0,
      total: rowTotal(rawDatasets, idxMap, i),
    });
  }

  rows.sort((a, b) => b.total - a.total);
  const rowsWithPipeline = rows.filter(r => r.total > 0);
  const grandTotal = rowsWithPipeline.reduce((s, r) => s + r.total, 0);
  const segmentCount = rowsWithPipeline.length;

  const labels = rowsWithPipeline.map(r => r.label);
  const datasets = rawDatasets.map((ds, di) => {
    const newData = rowsWithPipeline.map(r => {
      if (di === idxMap.iConf) return r.conf;
      if (di === idxMap.iTent) return r.tent;
      if (di === idxMap.iWait) return r.wait;
      return 0;
    });
    return { ...ds, data: newData };
  });

  const rowTotals = rowsWithPipeline.map(r => r.total);

  return { labels, datasets, rowTotals, grandTotal, segmentCount };
}

function computeSegmentKpis(labels, rowTotals, grandTotal, segmentCount) {
  if (!labels.length) {
    return { topSegment: '—', largestContribution: '—', segmentCountDisplay: '—', pipelineMix: '—' };
  }

  const topRev = rowTotals[0] || 0;
  const pct = grandTotal > 0 && topRev > 0 ? `${Math.round((topRev / grandTotal) * 1000) / 10}%` : '—';

  const mixParts = [];
  const top3 = labels.slice(0, 3);
  top3.forEach((label, i) => {
    const p = grandTotal > 0 ? Math.round((rowTotals[i] / grandTotal) * 100) : 0;
    mixParts.push(`${label.split(' ')[0]} ${p}%`);
  });

  return {
    topSegment: String(labels[0] ?? '—'),
    largestContribution: pct,
    segmentCountDisplay: String(segmentCount),
    pipelineMix: mixParts.join(' · ') || '—',
  };
}

function getSeriesColor(label) {
  const key = norm(label);
  if (key.includes('confirm')) return STATUS_COLORS.confirmed;
  if (key.includes('tent')) return STATUS_COLORS.tentative;
  if (key.includes('wait')) return STATUS_COLORS.waitlisted;
  return '#90a4ae';
}

const SegmentWiseBobChart = ({ data, name, handleExport, showButton = true }) => {
  const displayName = data?.name || name;

  const prepared = useMemo(() => prepareSegmentDisplay(data), [data]);

  const kpis = useMemo(
    () => computeSegmentKpis(prepared.labels, prepared.rowTotals, prepared.grandTotal, prepared.segmentCount),
    [prepared],
  );

  const option = useMemo(() => {
    const { labels, datasets, rowTotals, grandTotal } = prepared;

    const series = datasets.map(ds => ({
      name: ds.label || '',
      type: 'bar',
      stack: 'total',
      barWidth: '50%',
      itemStyle: { color: getSeriesColor(ds.label), borderRadius: 0 },
      emphasis: { itemStyle: { opacity: 0.85 } },
      data: ds.data,
    }));

    return {
      grid: standardGrid,
      tooltip: {
        ...premiumTooltip,
        trigger: 'axis',
        formatter(params) {
          if (!Array.isArray(params) || params.length === 0) return '';
          const idx = params[0].dataIndex;
          const total = rowTotals[idx] || 0;
          const pct = grandTotal > 0 ? Math.round((total / grandTotal) * 1000) / 10 : 0;
          const header = `<div style="font-weight:600;font-size:13px;color:#f8fafc;margin-bottom:6px">${labels[idx]} — ${formatRupeesIndianCompact(total)} (${pct}%)</div>`;
          const rows = params
            .filter(p => p.value != null && p.value !== 0)
            .map(p => {
              const marker = `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${p.color};margin-right:6px"></span>`;
              return `<div style="display:flex;justify-content:space-between;align-items:center;gap:12px">${marker}<span style="flex:1">${p.seriesName}</span><span style="font-weight:600;font-variant-numeric:tabular-nums">${formatRupeesIndianCompact(p.value)}</span></div>`;
            })
            .join('');
          return header + rows;
        },
      },
      xAxis: {
        ...indianCategoryAxis,
        data: labels,
      },
      yAxis: indianValueAxis,
      series,
      ...defaultAnimation,
    };
  }, [prepared]);

  const apiKpis = kpiMapToItems(data?.kpiMap);
  const infoTooltip = data?.infoText || 'Segment contribution to total pipeline';

  return (
    <Paper sx={reportChartPaperSx}>
      <ReportCardHeader
        title={displayName}
        insight={infoTooltip}
        handleExport={handleExport}
        showButton={showButton}
      />

      <ReportKpiStrip
        columns={4}
        items={apiKpis || [
          { label: 'Top Segment', value: kpis.topSegment },
          { label: 'Largest Contribution', value: kpis.largestContribution },
          { label: 'Pipeline Mix', value: kpis.pipelineMix },
        ]}
      />

      <ReportInsightBanner text={data?.insightText} style={data?.insightStyle} />

      <Box sx={reportChartCanvasSx}>
        <ReactECharts
          option={option}
          style={{ width: '100%', height: '100%' }}
          notMerge
          lazyUpdate
        />
      </Box>
    </Paper>
  );
};

export default SegmentWiseBobChart;
